import React from 'react';
import { useT } from '../../lib/lessonI18n';

// Airstream channel: upper/lower articulators + animated air particles for each manner of articulation.
const CH = {
  plosive: { gap: 0, burst: true },
  affricate: { gap: 0, burst: true, fric: true },
  fricative: { gap: 3, fric: true },
  nasal: { gap: 0, nose: true },
  approximant: { gap: 14 },
  lateral: { gap: 0, lateral: true },
  tap: { gap: 10, tap: true },
};

const CAPTION = {
  plosive: { it: 'chiusura → pressione → esplosione', en: 'closure → pressure → burst' },
  affricate: { it: 'chiusura → rilascio con attrito', en: 'closure → release with friction' },
  fricative: { it: 'restringimento → turbolenza', en: 'narrowing → turbulence' },
  nasal: { it: 'bocca chiusa · velo basso · aria dal naso', en: 'mouth closed · velum lowered · air through nose' },
  approximant: { it: 'canale aperto · nessun attrito', en: 'open channel · no friction' },
  lateral: { it: 'vista dall\'alto: contatto centrale, aria ai lati', en: 'top view: central contact, air at sides' },
  tap: { it: 'un solo colpo rapidissimo', en: 'single very quick contact' },
};

const Particles = ({ path, n = 7, dur = 1.6, color = '#22d3ee', jitter }) => Array.from({ length: n }, (_, i) => (
  <circle key={i} r={jitter ? 1.3 : 1.6} fill={color} opacity="0.9">
    <animateMotion dur={`${dur}s`} repeatCount="indefinite" begin={`${(i * dur) / n}s`} path={path} />
    {jitter && <animate attributeName="r" values="1;2.2;0.8;1.6" dur="0.25s" repeatCount="indefinite" />}
  </circle>
));

// Air held behind the closure, then released (synced with the 1.6s release cycle of the lower wall).
const Burst = ({ color }) => (
  <>
    {Array.from({ length: 6 }, (_, i) => (
      <circle key={`a${i}`} r="1.6" fill="#22d3ee" opacity="0.9">
        <animateMotion dur="1.6s" repeatCount="indefinite" path={`M${i * 14},50 L${100 - i * 3},50`} keyPoints="0;1;1;0" keyTimes="0;0.55;0.65;1" calcMode="linear" />
        <animate attributeName="opacity" values="0.9;0.9;0;0" keyTimes="0;0.64;0.66;1" dur="1.6s" repeatCount="indefinite" />
      </circle>
    ))}
    {Array.from({ length: 6 }, (_, i) => (
      <circle key={`b${i}`} r="1.6" fill={color} opacity="0">
        <animateMotion dur="1.6s" repeatCount="indefinite" path={`M134,50 L${190 + i * 5},${44 + i * 2.4}`} keyPoints="0;0;1" keyTimes="0;0.65;1" calcMode="linear" />
        <animate attributeName="opacity" values="0;0;1;0" keyTimes="0;0.64;0.7;1" dur="1.6s" repeatCount="indefinite" />
      </circle>
    ))}
  </>
);

const Lateral = () => (
  <>
    <path d="M0,26 L220,26 M0,74 L220,74" stroke="rgba(148,163,184,0.5)" strokeWidth="1.4" strokeDasharray="4 3" />
    <path d="M60,62 Q60,38 100,36 L150,36 Q160,50 150,64 L100,64 Q60,62 60,62 Z" fill="rgba(244,114,182,0.15)" stroke="#f472b6" strokeWidth="1.2" />
    <ellipse cx="120" cy="50" rx="10" ry="12" fill="#f472b6" opacity="0.6" />
    <Particles path="M0,50 L100,50 Q108,30 120,30 Q132,30 140,50 L220,50" n={6} dur={1.8} />
    <Particles path="M0,50 L100,50 Q108,70 120,70 Q132,70 140,50 L220,50" n={6} dur={1.8} color="#67e8f9" />
  </>
);

export const MannerDiagram = ({ manner }) => {
  const T = useT();
  const c = CH[manner] || CH.approximant;
  const top = 50 - c.gap / 2;
  const bot = 50 + c.gap / 2;
  const caption = CAPTION[manner] || CAPTION.approximant;
  return (
    <svg viewBox="0 0 220 110" className="w-full h-full" data-testid={`manner-diagram-${manner}`} role="img" aria-label={manner}>
      <text x="6" y="12" fontSize="6" fill="#64748b" fontWeight="700">{T('POLMONI →', 'LUNGS →')}</text>
      <text x="166" y="12" fontSize="6" fill="#64748b" fontWeight="700">{T('→ BOCCA', '→ MOUTH')}</text>
      {c.lateral ? <Lateral /> : (
        <>
          <path d={`M0,30 L80,30 Q100,30 110,${top} L130,${top} Q140,30 160,30 L220,30`} fill="none" stroke="#e2e8f0" strokeWidth="2.4" className={c.tap ? 'ct-tap' : ''} />
          <path d={`M0,70 L80,70 Q100,70 110,${bot} L130,${bot} Q140,70 160,70 L220,70`} fill="none" stroke="#f472b6" strokeWidth="2.4" className={c.burst ? 'ct-release' : ''} />
        </>
      )}
      {c.nose && (
        <>
          <path d="M96,30 L96,8 L220,8" fill="none" stroke="rgba(34,211,238,0.5)" strokeWidth="1.4" />
          <text x="150" y="5" fontSize="6" fill="#22d3ee" fontWeight="700">{T('NASO', 'NOSE')}</text>
          <Particles path="M0,50 L92,50 L92,18 L220,18" n={8} dur={2} />
        </>
      )}
      {!c.nose && !c.lateral && c.gap > 0 && <Particles path="M0,50 L220,50" n={c.fric ? 10 : 7} dur={c.fric ? 1.2 : 1.8} jitter={c.fric} color={c.fric ? '#fb923c' : '#22d3ee'} />}
      {c.burst && <Burst color={c.fric ? '#fb923c' : '#fde047'} />}
      {c.burst && <circle cx="132" cy="50" r="4" fill="none" stroke="#fde047" strokeWidth="1.2" className="ct-burst" />}
      <text x="110" y="104" fontSize="7" textAnchor="middle" fill="#cbd5e1" fontWeight="700">{T(caption.it, caption.en)}</text>
    </svg>
  );
};
